/**
 * Bottom sheet that lets the user pick an arbitrary set of sharers for a
 * single line (overrides the implicit tap toggle). Confirms call
 * `dispatch({ type: 'set_split', lineId, sharers })` on the parent.
 */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { BottomSheet } from "@/components/group/BottomSheet";
import { Avatar } from "@/components/group/Avatar";
import type { GroupCartParticipant } from "@/api/group-cart";

export interface SplitSheetProps {
  open: boolean;
  onClose: () => void;
  lineId: string | null;
  lineLabel: string;
  lineTotal: number;
  participants: GroupCartParticipant[];
  selfId: string;
  initialSharers: string[];
  format: (amount: number) => ReactNode;
  onConfirm: (sharers: string[]) => void;
}

export function SplitSheet({
  open,
  onClose,
  lineId,
  lineLabel,
  lineTotal,
  participants,
  selfId,
  initialSharers,
  format,
  onConfirm,
}: SplitSheetProps) {
  const { t } = useTranslation();
  const [picks, setPicks] = useState<string[]>(initialSharers);

  // Re-seed picks when the parent opens the sheet for a different line.
  useEffect(() => {
    if (open) setPicks(initialSharers);
  }, [open, lineId, initialSharers]);

  const toggle = (id: string) => {
    setPicks((cur) =>
      cur.includes(id) ? cur.filter((s) => s !== id) : [...cur, id],
    );
  };

  const eachPays = picks.length > 0 ? lineTotal / picks.length : 0;

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      ariaLabel={t("v2.split_pay.split_with")}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "var(--ink-1)" }}>
            {t("v2.split_pay.split_with")}
          </div>
          <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 2 }}>
            {lineLabel} · {format(lineTotal)}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
          }}
        >
          {participants.map((p) => {
            const id = p.guestId;
            const checked = picks.includes(id);
            return (
              <button
                key={id}
                onClick={() => toggle(id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: checked
                    ? "1.5px solid var(--primary-600)"
                    : "1px solid var(--border)",
                  background: checked
                    ? "var(--primary-50)"
                    : "var(--surface-1)",
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "inherit",
                }}
              >
                <Avatar participant={p} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--ink-1)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {id === selfId
                      ? t("v2.split_pay.share_label_self")
                      : p.displayName}
                  </div>
                </div>
                {checked && (
                  <span
                    aria-hidden
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 999,
                      background: "var(--primary-600)",
                      color: "#fff",
                      display: "grid",
                      placeItems: "center",
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {picks.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 13,
              color: "var(--ink-2)",
              padding: "4px 4px 0",
            }}
          >
            <span>{t("v2.split_pay.each_pays")}</span>
            <span style={{ fontWeight: 800, color: "var(--ink-1)" }}>
              {format(eachPays)}
            </span>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              height: 46,
              borderRadius: 12,
              background: "var(--surface-2)",
              border: "1px solid var(--border)",
              fontSize: 14,
              fontWeight: 700,
              color: "var(--ink-1)",
              cursor: "pointer",
            }}
          >
            {t("v2.split_pay.cancel")}
          </button>
          <button
            onClick={() => {
              onConfirm(picks);
              onClose();
            }}
            style={{
              flex: 2,
              height: 46,
              borderRadius: 12,
              background: "var(--primary-600)",
              border: "none",
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {t("v2.split_pay.confirm_split")}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
